import React from 'react';
import { useRouter } from 'next/router';
import { useAppDispatch } from 'store/hooks';
import { setSectionDrafts } from 'store/ScriptStudioBuffersSlice';
import { useCreateProject } from 'components/Projects/graphql.mutations';
import useOnProjectCreationSuccess, {
  AVATAR_MODAL,
  IMPORT_MODAL,
} from '../../../Projects/useOnProjectCreationSuccess';
import { StudioProjectVersion } from '../../../../../server/graphql/__generated.types';
import fetch from '../../../../lib/fetch';
import StartModal from '../StartModal';
import ImportModal, { type ImportModalSubmission } from '../ImportModal';
import ImportModalSetup from '../ImportModalSetup';
import type { ImportModalSetupSection } from '../ImportModalSetup/guidedProjectCreationProgress';

const DEFAULT_PROJECT_NAME = 'New project';

export type GuidedImportFlowProps = {
  close: () => void;
  projectId?: string;
};

type Phase =
  | { name: 'start'; errorReason?: string }
  | { name: 'import'; errorReason?: string }
  | { name: 'setup'; errorReason?: string };

async function startGuidedProjectCreation(
  projectId: string,
  submission: ImportModalSubmission
) {
  const isJson = submission.method === 'paste' || submission.method === 'url';
  let body: BodyInit;
  if (submission.method === 'paste') {
    body = JSON.stringify({ text: submission.text });
  } else if (submission.method === 'url') {
    body = JSON.stringify({ url: submission.url });
  } else {
    const formData = new FormData();
    formData.append('file', submission.file, submission.file.name);
    body = formData;
  }

  const response = await fetch(`/api/guided-project-creation/${projectId}`, {
    method: 'POST',
    headers: isJson ? { 'Content-Type': 'application/json' } : {},
    body,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => undefined);
    throw new Error(
      error?.message ?? 'Failed to start guided project creation'
    );
  }

  response.body?.cancel();
}

/**
 * Orchestrates guided import with either a new or existing project.
 * If projectId is provided, imports into that project and closes the modal.
 * Otherwise, creates a new universal project, imports, and navigates into it.
 */
function GuidedImportFlow({
  close,
  projectId: initialProjectId,
}: GuidedImportFlowProps) {
  const router = useRouter();
  const appDispatch = useAppDispatch();
  const [createProject] = useCreateProject({ source: 'guided_import' });
  const onProjectCreationSuccess = useOnProjectCreationSuccess();
  const [phase, setPhase] = React.useState<Phase>(() =>
    initialProjectId ? { name: 'import' } : { name: 'start' }
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleStartImport = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await createProject({
        variables: {
          inputs: {
            name: DEFAULT_PROJECT_NAME,
            clientName: '',
            studioVersion: StudioProjectVersion.Universal,
          },
        },
        errorPolicy: 'all',
      });
      const projectId = result.data?.createProject?.project?.id;
      if (!result.data?.createProject?.success || !projectId) {
        throw new Error(
          result.data?.createProject?.message ?? 'Failed to create project'
        );
      }
      await onProjectCreationSuccess({ projectId, targetModal: IMPORT_MODAL });
      setPhase({ name: 'import' });
    } catch (error: any) {
      setPhase({
        name: 'start',
        errorReason: error?.message || 'something went wrong.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartBlank = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await createProject({
        variables: {
          inputs: {
            name: DEFAULT_PROJECT_NAME,
            clientName: '',
            studioVersion: StudioProjectVersion.Universal,
          },
        },
        errorPolicy: 'all',
      });
      const projectId = result.data?.createProject?.project?.id;
      if (!result.data?.createProject?.success || !projectId) {
        throw new Error(
          result.data?.createProject?.message ?? 'Failed to create project'
        );
      }
      await onProjectCreationSuccess({ projectId, targetModal: AVATAR_MODAL });
      setPhase({ name: 'import' });
    } catch (error: any) {
      setPhase({
        name: 'start',
        errorReason: error?.message || 'something went wrong.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (
    projectId: string,
    submission: ImportModalSubmission
  ) => {
    try {
      await startGuidedProjectCreation(projectId, submission);
      setPhase({ name: 'setup' });
    } catch (error: any) {
      setPhase({
        name: 'import',
        errorReason: error?.message || 'Failed to import',
      });
    }
  };

  const handleSetupDone = (sections: ImportModalSetupSection[] = []) => {
    if (phase.name !== 'setup') return;
    if (!initialProjectId) return;
    if (sections.length) {
      appDispatch(
        setSectionDrafts({
          projectId: initialProjectId,
          sections: sections.map(section => ({
            id: section.id,
            plainText: section.text,
            formattedText: section.text,
          })),
        })
      );
    }
    if (initialProjectId) {
      close();
    } else {
      router?.push(`/dashboard/studio/${initialProjectId}`);
    }
  };

  const handleSetupFail = (reason: string) => {
    setPhase({ name: 'import', errorReason: reason });
  };

  if (phase.name === 'setup' && initialProjectId) {
    return (
      <ImportModalSetup
        open
        projectId={initialProjectId}
        close={close}
        onFail={handleSetupFail}
        onDone={handleSetupDone}
      />
    );
  }

  if (phase.name === 'import' && initialProjectId) {
    return (
      <ImportModal
        open
        ctaText={'Import'}
        close={() => close()}
        // casting for type satisfaction
        onSubmit={submission => onSubmit(initialProjectId, submission)}
        errorReason={phase.errorReason}
      />
    );
  }

  return (
    <StartModal
      open
      close={close}
      error={phase.errorReason}
      onImport={handleStartImport}
      onStartBlank={handleStartBlank}
    />
  );
}

export default GuidedImportFlow;
